import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import type { IModalDialogContent, ModalDialogOptions, ModalWindow } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';
import { ToggleFormButton } from '@components/ToggleFormButton.tsx';
import type { ShareSettings } from '../../models.ts';

type Props = { currentSettings?: ShareSettings };

const DEFAULT_SETTINGS: ShareSettings = {
  viewMode: 'default',
  showDirectLink: false,
  showCdnLink: false,
  showZipContent: true,
  showQrCode: true,
  description: null,
};

@observer
export class CustomizeShareDialog
  extends React.Component<Props>
  implements IModalDialogContent<ShareSettings>
{
  @observable private _viewMode: 'default' | 'strict';
  @observable private _showDirectLink: boolean;
  @observable private _showCdnLink: boolean;
  @observable private _showZipContent: boolean;
  @observable private _showQrCode: boolean;
  @observable private _description: string;

  constructor(props: Props) {
    super(props);
    const s = props.currentSettings ?? DEFAULT_SETTINGS;
    this._viewMode = s.viewMode;
    this._showDirectLink = s.showDirectLink;
    this._showCdnLink = s.showCdnLink;
    this._showZipContent = s.showZipContent;
    this._showQrCode = s.showQrCode;
    this._description = s.description ?? '';
    makeObservable(this);
  }

  getModalOptions(window: ModalWindow<ShareSettings>): ModalDialogOptions<ShareSettings> {
    return {
      title: 'Customize Share Page',
      width: '480px',
      closeByEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          title: 'Save',
          type: ModalButtonType.Add,
          color: 'primary',
          onClick: () => {
            window.setPayload({
              viewMode: this._viewMode,
              showDirectLink: this._showDirectLink,
              showCdnLink: this._showCdnLink,
              showZipContent: this._showZipContent,
              showQrCode: this._showQrCode,
              description: this._description.trim() || null,
            });
            window.close(ModalButtonType.Add);
          },
        },
        {
          title: 'Cancel',
          type: ModalButtonType.Cancel,
          onClick: () => window.close(ModalButtonType.Cancel),
        },
      ],
    };
  }

  render() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* View mode */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
            View mode
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`btn ${this._viewMode === 'default' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1 }}
              onClick={this._setDefault}
            >
              Default
            </button>
            <button
              className={`btn ${this._viewMode === 'strict' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1 }}
              onClick={this._setStrict}
            >
              Strict
            </button>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.65 }}>
            {this._viewMode === 'default'
              ? 'Current look — animated illustration, QR code toggle.'
              : 'Enterprise look — file type icon, upload info, no animations.'}
          </p>
        </div>

        {/* Direct links */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
            Direct links
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ToggleFormButton
              label="Show direct download link (API)"
              checked={this._showDirectLink}
              onChange={this._onToggleDirectLink}
            />
            <ToggleFormButton
              label="Show CDN / static-storage link"
              checked={this._showCdnLink}
              onChange={this._onToggleCdnLink}
            />
          </div>
          <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.65 }}>
            Makes raw URLs visible and copyable on the share page.
          </p>
        </div>

        {/* Zip content */}
        <div>
          <ToggleFormButton
            label="Show zip / archive contents"
            checked={this._showZipContent}
            onChange={this._onToggleZipContent}
          />
          <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.65 }}>
            When disabled, the file list inside archives is hidden from visitors.
          </p>
        </div>

        {/* QR code */}
        <div>
          <ToggleFormButton
            label="Show QR code"
            checked={this._showQrCode}
            onChange={this._onToggleQrCode}
          />
          <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.65 }}>
            QR code for this share link, visible on the download page.
          </p>
        </div>

        {/* Markdown description */}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>
            Description <span style={{ fontWeight: 400, opacity: 0.6 }}>(Markdown)</span>
          </label>
          <textarea
            className="form-control"
            rows={5}
            placeholder="Optional description shown on the share page. Supports **bold**, *italic*, lists, links…"
            value={this._description}
            onChange={this._onDescriptionChange}
            style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }}
          />
        </div>

      </div>
    );
  }

  @action.bound private _setDefault() { this._viewMode = 'default'; }
  @action.bound private _setStrict()  { this._viewMode = 'strict'; }

  @action.bound private _onToggleDirectLink(checked: boolean) { this._showDirectLink = checked; }
  @action.bound private _onToggleCdnLink(checked: boolean)    { this._showCdnLink = checked; }
  @action.bound private _onToggleZipContent(checked: boolean) { this._showZipContent = checked; }
  @action.bound private _onToggleQrCode(checked: boolean)    { this._showQrCode = checked; }

  @action.bound
  private _onDescriptionChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    this._description = e.target.value;
  }
}
